import { css } from 'aphrodite';
import * as React from 'react';
import { useContext } from 'react';
import { getDisplayName } from '../../../../models/NameUtils';
import { ViewportContext } from '../../../hooks/useViewport';
import { ILeaderboardUser } from '../../../viewModels/gamification';
import { styleSheet } from '../styles';
import { ListHeader, SortOptions } from './ListHeader';

interface IProps {
	asc?: boolean;
	selected?: SortOptions;
	setAsc?(asc: boolean): void;
	setSortBy?(sort: SortOptions): void;
	sortBy?: SortOptions;
	top10: ILeaderboardUser[];
}

const COLUMNS: SortOptions[] = [
	SortOptions.Points,
	SortOptions.Dials,
	SortOptions.ProspectingDials,
	SortOptions.Connections,
	SortOptions.Demos,
];

const COLUMN_COLOR_STYLE: Partial<Record<SortOptions, (typeof styleSheet)[keyof typeof styleSheet]>> = {
	[SortOptions.Points]: styleSheet.leaderCellPoints,
	[SortOptions.Connections]: styleSheet.leaderCellConnections,
};

export const LeaderboardList: React.FC<IProps> = ({ asc, selected, setAsc, setSortBy, sortBy, top10 }) => {
	const { isBelowSmall } = useContext(ViewportContext);

	if (top10.length <= 0) {
		return null;
	}

	const handleSort = (sort: SortOptions) => {
		if (sort !== sortBy) {
			setSortBy?.(sort);
		} else {
			setAsc?.(asc !== true);
		}
	};

	// Phones show one stat column at a time, picked from the column nav above the board.
	const columns = isBelowSmall ? COLUMNS.filter(key => key === selected) : COLUMNS;
	const rowStyles = [styleSheet.leaderRow, isBelowSmall && styleSheet.leaderRowSingleColumn];

	return (
		<div className={css(styleSheet.table)}>
			<div className={css(...rowStyles, styleSheet.leaderHeaderRow)}>
				<span>Rep</span>
				{columns.map(key => (
					<ListHeader key={key} columnKey={key} isAsc={asc === true} onClickToSort={handleSort} sortBy={sortBy} />
				))}
			</div>
			{top10.map(leader => {
				const userName = getDisplayName(leader.userReference);
				return (
					<div className={css(...rowStyles)} key={leader.userReference.id}>
						<span className={css(styleSheet.leaderName)} title={userName}>
							{userName}
						</span>
						{columns.map(key => (
							<span
								key={key}
								className={css(
									styleSheet.leaderCell,
									COLUMN_COLOR_STYLE[key],
									key === sortBy && styleSheet.leaderCellSorted
								)}
							>
								{leader.score[key] || 0}
							</span>
						))}
					</div>
				);
			})}
		</div>
	);
};
